<template>
  <div class="privacy-policy-page">
    <header>
      <h1>Privacy Policy</h1>
      <p class="app-names">AR Routes of Lesbos / AR Διαδρομές Λέσβου</p>
      <p class="muted">Effective date: 14 May 2026</p>
      <p class="muted">Last updated: 14 May 2026</p>
    </header>

    <p>
      This Privacy Policy explains how <strong>UniSystems SA</strong> (“
      <strong>we</strong>”, “<strong>us</strong>”) handles information when you
      use the mobile application <strong>AR Routes of Lesbos</strong> (also
      marketed as <strong>AR Διαδρομές Λέσβου</strong>) (the “
      <strong>App</strong>”). It is published at a stable URL for users and for
      Google Play, together with our
      <a
        href="https://support.google.com/googleplay/android-developer/answer/10787469"
        rel="noopener noreferrer"
        >Data safety</a
      >
      declarations in Play Console.
    </p>

    <p>
      <strong>Summary (aligned with Google Play Data safety):</strong> We state
      in Play Console that this App
      <strong>does not collect user data</strong> and
      <strong>does not share user data</strong> with other companies or
      organizations. This policy describes what that means in practice,
      including how on-device permissions work and how you can contact us.
    </p>

    <h2>1. Contact</h2>
    <p>
      <strong>Developer:</strong> UniSystems SA.<br />
      <strong>Privacy contact:</strong>
      <a href="mailto:efales@culture.gr">efales@culture.gr</a><br />
    </p>

    <h2>2. Data collection, sharing, and retention</h2>
    <p>
      We <strong>do not</strong> use the App to collect, upload to our servers,
      or store personal or sensitive user data (such as camera images, precise
      location history, contacts, or advertising identifiers) for our own
      purposes. We <strong>do not</strong> sell user data. We
      <strong>do not</strong> share App user data with third parties as part of
      the App’s operation, in line with our Play Console Data safety answers.
    </p>
    <p>
      Because we do not maintain user profiles or an in-App account database for
      the experiences described here, there is
      <strong>nothing for us to retain or delete</strong> on our systems in
      connection with typical App use. Uninstalling the App removes App data
      from your device according to your operating system.
    </p>

    <h2>3. Permissions and on-device processing</h2>
    <p>
      The App may request device permissions so features can run
      <strong>on your device</strong>. Granting a permission means the operating
      system allows the App to access that capability locally. That access is
      used to show maps, routes, augmented reality, or nearby experiences as
      designed; it is <strong>not</strong> described in this policy as “data
      collection” by us, because we do not receive or store that information on
      our backend for the App.
    </p>
    <ul>
      <li>
        <strong>Camera</strong> — Used for augmented reality (for example live
        preview and alignment). Video frames are processed on the device to
        render AR; we do not receive or store your camera feed on our servers.
      </li>

      <li>
        <strong>Network (Internet)</strong> — Used so the App can load content
        and updates .
      </li>
    </ul>

    <h2>4. When you email us</h2>
    <p>
      If you contact us at
      <a href="mailto:efales@culture.gr">efales@culture.gr</a>, we will use your
      email address and message content only to respond to your request. We do
      not use support correspondence for marketing.
    </p>

    <h2>5. Google Play and platform services</h2>
    <p>
      When you install or update the App through Google Play, Google processes
      information as described in
      <a href="https://policies.google.com/privacy" rel="noopener noreferrer"
        >Google’s Privacy Policy</a
      >. Our statements here concern the App as we publish it and our Data
      safety answers; they are designed to meet Google’s expectations for
      transparency under the
      <a
        href="https://support.google.com/googleplay/android-developer/answer/10144311"
        rel="noopener noreferrer"
        >User Data policy</a
      >.
    </p>

    <h2>6. Changes</h2>
    <p>
      We may update this policy from time to time. The “Last updated” date at
      the top will change when we do. If our data practices or Play Console Data
      safety answers change materially, we will update this page and the store
      listing as required.
    </p>

    <footer>
      <p class="muted">© 2026 UniSystems SA. All rights reserved.</p>
    </footer>
  </div>
</template>

<script>
const PAGE_TITLE = "Privacy Policy — AR Routes of Lesbos / AR Διαδρομές Λέσβου";

export default {
  name: "PrivacyPolicyEn",

  data() {
    return {
      previousTitle: "",
    };
  },

  mounted() {
    this.previousTitle = document.title;
    document.title = PAGE_TITLE;
  },

  beforeDestroy() {
    document.title = this.previousTitle || "";
  },
};
</script>

<style scoped>
.privacy-policy-page {
  color-scheme: light dark;
  --pp-text: #1a1a1a;
  --pp-muted: #444;
  --pp-bg: #fafafa;
  --pp-border: #ccc;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell,
    "Noto Sans", sans-serif;
  line-height: 1.55;
  max-width: 42rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 3rem;
  color: var(--pp-text);
  background: var(--pp-bg);
  min-height: 100vh;
  box-sizing: border-box;
}

@media (prefers-color-scheme: dark) {
  .privacy-policy-page {
    --pp-text: #e8e8e8;
    --pp-muted: #b0b0b0;
    --pp-bg: #121212;
    --pp-border: #444;
  }
}

.privacy-policy-page h1 {
  font-size: 1.5rem;
  margin-top: 0;
}

.privacy-policy-page h2 {
  font-size: 1.15rem;
  margin-top: 1.75rem;
  border-bottom: 1px solid var(--pp-border);
  padding-bottom: 0.25rem;
}

.privacy-policy-page p,
.privacy-policy-page li {
  color: var(--pp-text);
}

.privacy-policy-page .muted {
  color: var(--pp-muted);
  font-size: 0.95rem;
}

.privacy-policy-page .app-names {
  font-weight: 600;
}

.privacy-policy-page ul {
  padding-left: 1.25rem;
}

.privacy-policy-page a {
  color: inherit;
  text-decoration: underline;
}
</style>
